import React, { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useAdminI18n } from '../../i18n/adminI18n';

const isAuthOrPermissionError = (error: unknown) => {
  const value = error as any;
  const code = String(value?.code || value?.name || '').toLowerCase();
  const message = String(value?.message || value?.reason || value || '').toLowerCase();
  return (
    code.includes('permission-denied') ||
    code.includes('unauthenticated') ||
    code.includes('401') ||
    code.includes('403') ||
    message.includes('missing or insufficient permissions') ||
    message.includes('permission-denied') ||
    message.includes('unauthenticated') ||
    message.includes('access forbidden')
  );
};

const GlobalAuthIssueBanner: React.FC = () => {
  const { language, direction } = useAdminI18n();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const showIfAuthIssue = (error: unknown) => {
      if (isAuthOrPermissionError(error)) setVisible(true);
    };
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => showIfAuthIssue(event.reason);
    const handleError = (event: ErrorEvent) => showIfAuthIssue(event.error || event.message);

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  if (!visible) return null;

  const text = language === 'ar'
    ? {
        title: 'مشكلة صلاحيات أو جلسة',
        body: 'بعض البيانات لم تُحمّل لأن الجلسة أو الصلاحيات غير صالحة. حدّث الصفحة، وإذا استمرت المشكلة تأكد من دور المستخدم وقواعد Firestore.',
        dismiss: 'إخفاء',
      }
    : {
        title: 'Permission or session issue',
        body: 'Some live data could not load because the session or role is missing access. Refresh once; if it continues, check this user role and Firestore rules.',
        dismiss: 'Dismiss',
      };

  return (
    <div className="fixed inset-x-3 top-3 z-[100] mx-auto max-w-3xl" dir={direction}>
      <div className="flex items-start gap-3 rounded-2xl border border-red-100 bg-red-50 p-4 text-red-900 shadow-2xl">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-black">{text.title}</p>
          <p className="mt-1 text-sm font-semibold leading-5">{text.body}</p>
        </div>
        <button
          type="button"
          onClick={() => setVisible(false)}
          className="inline-flex min-h-9 min-w-9 items-center justify-center rounded-xl bg-white/80 text-red-700 hover:bg-white"
          aria-label={text.dismiss}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default GlobalAuthIssueBanner;
